import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { Chip, CircularProgress } from '@mui/material'
import { ReactElement } from 'react'
import { useIntl } from 'react-intl'
import messages from './messages'
import { SubdomainStatus } from './types'

// Live availability indicator next to the subdomain field. Per WCAG 1.4.1 the status is NOT conveyed by
// colour alone: each state carries an icon (check / x / warning) and the pill sits in an aria-live
// region so screen readers announce the changing status text.
type ResultStatus = Exclude<SubdomainStatus, 'idle' | 'checking'>

const RESULT_CONFIG: Record<ResultStatus, { color: 'success' | 'error' | 'warning'; label: typeof messages.available; icon: ReactElement }> = {
  available: { color: 'success', label: messages.available, icon: <CheckCircleIcon fontSize='small' /> },
  taken: { color: 'error', label: messages.taken, icon: <CancelIcon fontSize='small' /> },
  reserved: { color: 'error', label: messages.reserved, icon: <CancelIcon fontSize='small' /> },
  invalidFormat: { color: 'error', label: messages.invalidFormat, icon: <CancelIcon fontSize='small' /> },
  error: { color: 'warning', label: messages.pillError, icon: <WarningAmberIcon fontSize='small' /> },
}

const SubdomainAvailabilityPill = ({ status }: { status: SubdomainStatus }) => {
  const { formatMessage } = useIntl()

  const chip = (() => {
    if (status === 'idle') return null
    if (status === 'checking') {
      return <Chip size='small' icon={<CircularProgress size={12} />} label={formatMessage(messages.checking)} />
    }
    const { color, label, icon } = RESULT_CONFIG[status]
    return <Chip size='small' color={color} icon={icon} label={formatMessage(label)} />
  })()

  // Always-rendered live region so the status change is announced (not just the colour).
  return <span aria-live='polite'>{chip}</span>
}

export default SubdomainAvailabilityPill
